import PlatformBadge, { getPlatform } from '../PlatformBadge/PlatformBadge';
import { YT_TITLE_MAX } from '../../Utilities/uploadConstants';

/**
 * Renders the platform-specific fields for a single selected login.
 * Keeps UploadVideoModal readable by isolating the per-platform forms.
 */
const PlatformFields = ({ login, fields, error, onChange }) => {
  const label = getPlatform(login.platform)?.label || login.platform;

  let body = null;
  switch (login.platform) {
    case 'youtube':
      body = (
        <>
          <label className="uvm-field">
            <span>Title *</span>
            <input
              type="text"
              maxLength={YT_TITLE_MAX}
              className="uvm-input"
              value={fields.title}
              onChange={(e) => onChange({ title: e.target.value })}
              placeholder="My Short"
            />
            <span className="uvm-field-hint">
              {fields.title.length}/{YT_TITLE_MAX}
            </span>
          </label>
          <label className="uvm-field">
            <span>Visibility</span>
            <select
              className="uvm-input"
              value={fields.visibility}
              onChange={(e) => onChange({ visibility: e.target.value })}
            >
              <option value="public">Public</option>
              <option value="unlisted">Unlisted</option>
              <option value="private">Private</option>
            </select>
          </label>
          <label className="uvm-field uvm-field-check">
            <input
              type="checkbox"
              checked={fields.made_for_kids}
              onChange={(e) => onChange({ made_for_kids: e.target.checked })}
            />
            <span>Made for kids (COPPA)</span>
          </label>
          <label className="uvm-field">
            <span>Tags</span>
            <input
              type="text"
              className="uvm-input"
              value={fields.tags}
              onChange={(e) => onChange({ tags: e.target.value })}
              placeholder="shorts, fyp, comedy"
            />
            <span className="uvm-field-hint">Comma separated</span>
          </label>
        </>
      );
      break;

    case 'tiktok':
      body = (
        <>
          {login.sandbox_mode && (
            <div className="uvm-banner">
              Your TikTok app is in sandbox mode — posts will be private
              regardless of this setting.
            </div>
          )}
          <label className="uvm-field">
            <span>Privacy *</span>
            <select
              className="uvm-input"
              value={fields.privacy_level}
              onChange={(e) => onChange({ privacy_level: e.target.value })}
            >
              <option value="PUBLIC_TO_EVERYONE">Everyone</option>
              <option value="MUTUAL_FOLLOW_FRIENDS">Mutual follow friends</option>
              <option value="SELF_ONLY">Only me</option>
            </select>
          </label>
          <div className="uvm-field-grid">
            <label className="uvm-field uvm-field-check">
              <input
                type="checkbox"
                checked={!fields.disable_comment}
                onChange={(e) => onChange({ disable_comment: !e.target.checked })}
              />
              <span>Allow comments</span>
            </label>
            <label className="uvm-field uvm-field-check">
              <input
                type="checkbox"
                checked={!fields.disable_duet}
                onChange={(e) => onChange({ disable_duet: !e.target.checked })}
              />
              <span>Allow duet</span>
            </label>
            <label className="uvm-field uvm-field-check">
              <input
                type="checkbox"
                checked={!fields.disable_stitch}
                onChange={(e) => onChange({ disable_stitch: !e.target.checked })}
              />
              <span>Allow stitch</span>
            </label>
            <label className="uvm-field uvm-field-check">
              <input
                type="checkbox"
                checked={fields.brand_content_toggle}
                onChange={(e) =>
                  onChange({ brand_content_toggle: e.target.checked })
                }
              />
              <span>Branded content</span>
            </label>
            <label className="uvm-field uvm-field-check">
              <input
                type="checkbox"
                checked={fields.brand_organic_toggle}
                onChange={(e) =>
                  onChange({ brand_organic_toggle: e.target.checked })
                }
              />
              <span>Your own brand</span>
            </label>
          </div>
        </>
      );
      break;

    case 'instagram':
      body = (
        <>
          <label className="uvm-field uvm-field-check">
            <input
              type="checkbox"
              checked={fields.share_to_feed}
              onChange={(e) => onChange({ share_to_feed: e.target.checked })}
            />
            <span>Share to main feed (as well as Reels tab)</span>
          </label>
          <label className="uvm-field">
            <span>Cover frame timestamp (ms)</span>
            <input
              type="number"
              min={0}
              className="uvm-input"
              value={fields.cover_frame_ms}
              onChange={(e) => onChange({ cover_frame_ms: e.target.value })}
              placeholder="Instagram picks one if blank"
            />
          </label>
          <div className="uvm-hint">Media type fixed to Reels in v1.</div>
        </>
      );
      break;

    case 'facebook':
      body = (
        <label className="uvm-field">
          <span>Description (falls back to caption)</span>
          <textarea
            className="uvm-textarea"
            rows={3}
            value={fields.description}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </label>
      );
      break;

    default:
      return null;
  }

  return (
    <div className="uvm-section uvm-platform-fields">
      <div className="uvm-section-label">
        <PlatformBadge platform={login.platform} size="sm" />
        <span>{label} options</span>
        <span className="uvm-platform-fields-user">{login.platform_username}</span>
      </div>
      {body}
      {error && <div className="uvm-error-inline">{error}</div>}
    </div>
  );
};

export default PlatformFields;
